import { FC } from "react";
import classNames from "classnames";

import { ChartLabel } from "../ContentBuilder/ChartLabel/ChartLabel";

interface ResponsiveSvgImageProps {
	id?: string;
	desktopSrc: string;
	mobileSrc: string;
	alt?: string;
	breakpoint?: "sm" | "md" | "lg";
	title?: string;
	subtitle?: string;
	chartLabel?: string | null;
	containerClassName?: string;
	desktopClassName?: string;
	mobileClassName?: string;
}

const breakpointClasses = {
	sm: { desktop: "hidden sm:block", mobile: "block sm:hidden" },
	md: { desktop: "hidden md:block", mobile: "block md:hidden" },
	lg: { desktop: "hidden lg:block", mobile: "block lg:hidden" },
} as const;

const ResponsiveSvgImage: FC<ResponsiveSvgImageProps> = ({
	id,
	desktopSrc,
	mobileSrc,
	alt,
	breakpoint = "md",
	title,
	subtitle,
	chartLabel,
	containerClassName = "flex flex-col items-center justify-center",
	desktopClassName,
	mobileClassName,
}) => {
	const imgProps = alt ? { alt, title: alt } : { role: "presentation" as const };
	const hasContent = (value?: string) => typeof value === "string" && value.trim().length > 0;
	const hasHeading = hasContent(title) || hasContent(subtitle);

	return (
		<div id={id} className={containerClassName}>
			{hasHeading && (
				<div className="self-start w-full mb-4 flex flex-col gap-1">
					<ChartLabel label={chartLabel} />
					{/* Sized to match the in-chart title/subtitle pair the custom chart blocks render,
					    so a figure built from an SVG reads the same as one drawn by Recharts. */}
					{hasContent(title) && <h3 className="text-lg text-gray-800 font-bold">{title}</h3>}
					{hasContent(subtitle) && <h4 className="text-base text-gray-800">{subtitle}</h4>}
				</div>
			)}
			<img
				className={classNames(breakpointClasses[breakpoint].desktop, desktopClassName)}
				src={desktopSrc}
				{...imgProps}
			/>
			<img
				className={classNames(breakpointClasses[breakpoint].mobile, mobileClassName)}
				src={mobileSrc}
				{...imgProps}
			/>
		</div>
	);
};

export { ResponsiveSvgImage };
